class ImageUploadFeeSync extends HTMLElement {
  connectedCallback() {
    this.feeVariantId = parseInt(this.dataset.feeVariantId || '0', 10);
    if (!this.feeVariantId) return;

    this.syncInFlight = false;
    this.handleCartUpdate = this.handleCartUpdate.bind(this);

    this.cartUpdateUnsubscriber = null;
    if (theme?.pubsub?.subscribe && theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
      this.cartUpdateUnsubscriber = theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        this.handleCartUpdate
      );
    }

    this.sync();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  handleCartUpdate(event) {
    if (!event || event.source === 'image-upload-fee') return;
    if (event.cart) {
      this.syncWithCart(event.cart);
    }
  }

  async sync() {
    const cart = await (await fetch(`${theme.routes.cart_url}.js`, { credentials: 'same-origin' })).json();
    await this.syncWithCart(cart);
  }

  async syncWithCart(cart) {
    if (this.syncInFlight) return;
    this.syncInFlight = true;

    try {
      const targetQuantity = this.getUploadedImageCount(cart.items);

      const feeLine = cart.items.find((item) => Number(item.variant_id) === this.feeVariantId);
      const currentQuantity = feeLine ? feeLine.quantity : 0;

      if (currentQuantity === targetQuantity) return;
      this.applyOptimisticUI(cart, feeLine, currentQuantity, targetQuantity);

      const sections = Array.from(document.querySelectorAll('cart-items[data-section-id]'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);

      let response;
      if (feeLine) {
        response = await fetch(`${theme.routes.cart_change_url}`, {
          ...theme.utils.fetchConfig(),
          body: JSON.stringify({
            id: feeLine.key,
            quantity: targetQuantity,
            sections
          })
        });
      } else {
        response = await fetch(`${theme.routes.cart_add_url}`, {
          ...theme.utils.fetchConfig(),
          body: JSON.stringify({
            items: [
              {
                id: this.feeVariantId,
                quantity: targetQuantity,
                properties: {
                  _image_upload_fee: 'true'
                }
              }
            ],
            sections
          })
        });
      }

      const parsedState = await response.json();

      if (theme?.pubsub?.publish && theme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'image-upload-fee',
          cart: parsedState
        });
      }
    } catch (error) {
      console.error('Image upload fee sync failed', error);
    } finally {
      this.syncInFlight = false;
    }
  }

  getUploadedImageCount(items) {
    let count = 0;

    items.forEach((item) => {
      if (Number(item.variant_id) === this.feeVariantId) return;
      if (!item.properties) return;

      let itemImageCount = 0;
      Object.entries(item.properties).forEach(([key, value]) => {
        if (!key || !value) return;

        const normalizedValue = String(value);
        const isUploadedFile =
          normalizedValue.includes('/uploads/')
          || normalizedValue.includes('/files/')
          || normalizedValue.includes('cdn.shopify.com');

        if (isUploadedFile) itemImageCount += 1;
      });

      count += itemImageCount * Math.max(Number(item.quantity) || 1, 1);
    });

    return count;
  }

  applyOptimisticUI(cart, feeLine, currentQuantity, targetQuantity) {
    const delta = targetQuantity - currentQuantity;
    if (!delta) return;

    const unitPrice = feeLine ? Number(feeLine.final_price) || 0 : 0;
    const predictedTotal = cart.total_price + (delta * unitPrice);
    const predictedCount = cart.item_count + delta;

    const moneyFormat =
      window.themeVariables?.settings?.moneyWithCurrencyFormat
      || window.theme?.settings?.moneyWithCurrencyFormat
      || window.theme?.settings?.moneyFormat
      || '${{amount}}';

    if (window.theme?.Currency?.formatMoney) {
      const money = theme.Currency.formatMoney(predictedTotal, moneyFormat);
      document.querySelectorAll('.totals__subtotal-value').forEach((el) => {
        el.innerHTML = money;
      });
    }

    document.querySelectorAll('cart-count').forEach((el) => {
      el.textContent = String(Math.max(predictedCount, 0));
      if (predictedCount > 0) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });

    const feeInputs = document.querySelectorAll(`input[data-quantity-variant-id="${this.feeVariantId}"]`);
    feeInputs.forEach((input) => {
      input.value = String(targetQuantity);
    });
  }
}

if (!customElements.get('image-upload-fee-sync')) {
  customElements.define('image-upload-fee-sync', ImageUploadFeeSync);
}
